import { useLocation } from "react-router-dom";
import { useDeskSpend } from "../context/DeskSpendContext";

function formatUsd(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function isPhase1Search(pathname: string): boolean {
  return pathname === "/search" || pathname.startsWith("/search/");
}

export function DeskSpendTicker() {
  const { pathname } = useLocation();
  const { spend, loading } = useDeskSpend();
  const phase1 = isPhase1Search(pathname);
  const pct =
    spend.budget_usd > 0
      ? Math.min(100, (spend.spend_usd / spend.budget_usd) * 100)
      : 0;
  const budgetLocked = spend.desk_locked;
  const warn = budgetLocked || pct >= 90;
  const processed = spend.evidence_count;
  const eligible = spend.evidence_eligible ?? 0;
  const pending = spend.evidence_pending ?? 0;
  const offTarget = spend.evidence_off_target ?? 0;
  const showEvidence =
    !phase1 &&
    (eligible > 0 ||
      processed > 0 ||
      pending > 0 ||
      offTarget > 0 ||
      spend.evidence_cap_reached);

  return (
    <div
      className={`mx-3 mb-2 px-3 py-2 border ${
        budgetLocked
          ? "border-error/60 bg-error/10"
          : warn
            ? "border-error/60 bg-error/10"
            : "border-outline-variant/60 bg-surface-container"
      }`}
      title="Live API spend for the current canonical job"
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-data-label text-[10px] uppercase tracking-widest text-on-surface-variant">
          API spend
        </span>
        {loading ? (
          <span className="text-[10px] text-outline-variant">…</span>
        ) : null}
      </div>
      <div
        className={`text-lg font-black tabular-nums mt-0.5 ${
          warn ? "text-error" : "text-primary"
        }`}
      >
        {formatUsd(spend.spend_usd)}
      </div>
      <div className="text-[10px] text-on-surface-variant mt-0.5 tabular-nums">
        of {formatUsd(spend.budget_usd)} cap
        {budgetLocked ? (
          <span className="block mt-1 text-error font-semibold">
            Budget exceeded — paid actions disabled
          </span>
        ) : null}
        {showEvidence ? (
          <span className="block mt-0.5">
            {eligible > 0
              ? `${processed}/${eligible} extracted · ${pending} pending`
              : `${processed}/${spend.evidence_cap} extracted`}
            {offTarget > 0 ? ` · ${offTarget} off-target` : ""}
            {eligible > 0 ? (
              <span className="block mt-0.5 text-outline-variant">
                {spend.evidence_cap} page extraction cap
              </span>
            ) : null}
          </span>
        ) : null}
      </div>
      <div className="h-1 mt-2 bg-surface-variant overflow-hidden">
        <div
          className={`h-full transition-all ${warn ? "bg-error" : "bg-primary"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
